import {
  Box,
  Button,
  DateFormats,
  DateInput,
  Dropdown,
  Inline,
  type OptionType,
  Stack,
} from '@omnipresentgroup/design-system';
import { memo } from 'react';
import { Controller, useFieldArray, useFormContext } from 'react-hook-form';

import { TIMESLOT_TYPES_CONFIG } from '../constants';

const findOption = (options: OptionType[], selectedOption?: OptionType) =>
  options?.find(({ value }) => value === selectedOption?.value);

const timeSlotsOptions: OptionType[] = [...TIMESLOT_TYPES_CONFIG].map(
  (item) => {
    return {
      value: item[0],
      label: item[1].label,
      icon: item[1].icon,
    };
  },
);

type Props = {
  dayIndex: number;
  timeSlotDay?: string;
};

type FormData = {
  days: {
    timeSlots: {
      startTime?: Date | null | undefined;
      endTime?: Date | null | undefined;
      type?: OptionType;
    }[];
  }[];
};

export const TimesheetTimeSlotsFields = memo(({ dayIndex }: Props) => {
  const { control, formState } = useFormContext<FormData>();
  const { fields, remove, append } = useFieldArray({
    control,
    name: `days.${dayIndex}.timeSlots`,
  });

  const removeTimeSlot = (fieldIndex: number) => {
    remove(fieldIndex);

    if (fieldIndex === 0) {
      append({
        startTime: undefined,
        endTime: undefined,
        type: { label: '', value: '' },
      });
    }
  };

  return (
    <Stack gap="12">
      {fields.map(({ id }, fieldIndex) => {
        const fieldErrors =
          formState.errors?.days?.[dayIndex]?.timeSlots?.[fieldIndex];
        return (
          <Inline key={id} gap="8">
            <Controller
              control={control}
              name={`days.${dayIndex}.timeSlots.${fieldIndex}.startTime`}
              render={({ field }) => (
                <Box w={100}>
                  <DateInput
                    id={`day-${dayIndex}-${fieldIndex}-startTime`}
                    size="small"
                    showTimeSelect
                    showTimeSelectOnly
                    timeIntervals={5}
                    dateFormat={DateFormats.Time24h}
                    timeFormat={DateFormats.Time24h}
                    required
                    selected={field.value}
                    onChange={(date) => {
                      field.onChange(date);
                    }}
                    errorMessage={fieldErrors?.startTime?.message}
                  />
                </Box>
              )}
            />
            -
            <Controller
              control={control}
              name={`days.${dayIndex}.timeSlots.${fieldIndex}.endTime`}
              render={({ field }) => (
                <Box w={100}>
                  <DateInput
                    id={`days.${dayIndex}.day-${dayIndex}-${fieldIndex}-endTime`}
                    size="small"
                    showTimeSelect
                    showTimeSelectOnly
                    dateFormat={DateFormats.Time24h}
                    timeFormat={DateFormats.Time24h}
                    timeIntervals={5}
                    required
                    selected={field.value}
                    onChange={(date) => {
                      field.onChange(date);
                    }}
                    errorMessage={fieldErrors?.endTime?.message}
                  />
                </Box>
              )}
            />
            <Controller
              control={control}
              name={`days.${dayIndex}.timeSlots.${fieldIndex}.type`}
              render={({ field }) => (
                <Box minW={200} maxW={200}>
                  <Dropdown
                    id={`days.${dayIndex}.day-${dayIndex}-${fieldIndex}-type`}
                    size="small"
                    required
                    options={timeSlotsOptions}
                    onChange={(option) => {
                      field.onChange(option as OptionType);
                    }}
                    value={findOption(timeSlotsOptions, field.value)}
                    // @ts-ignore type as a prop name creates a conflict
                    errorMessage={fieldErrors?.type?.message}
                  />
                </Box>
              )}
            />
            <Button
              icon="Minus"
              size="small"
              variant="tertiary"
              onClick={() => {
                removeTimeSlot(fieldIndex);
              }}
            />
            {fieldIndex === fields.length - 1 ? (
              <Button
                icon="Plus"
                size="small"
                variant="tertiary"
                onClick={() => append({})}
              />
            ) : (
              <Box w={24} h={24} />
            )}
          </Inline>
        );
      })}
    </Stack>
  );
});
